import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabase";
import type { Session } from "@supabase/supabase-js";
import type { Subscription, SubscriptionPlan, SubscriptionStatus } from "../types";

import { hasComplimentaryAccess, resolveAccessLevel, type AccessLevel } from "../utils/subscriptionAccess";
export type { AccessLevel } from "../utils/subscriptionAccess";

export interface SubscriptionInfo {
  plan: SubscriptionPlan;
  status: SubscriptionStatus | string;
  accessLevel: AccessLevel;
  isActive: boolean;
  isComplimentary: boolean;
  endDate: string | null;
  trialEnd: string | null;
  subscription: Subscription | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

export function useSubscription(session: Session | null): SubscriptionInfo {
  const [sub, setSub] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reqIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchSub = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    const userId = session?.user?.id;

    if (!userId) {
      setSub(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (!mountedRef.current || reqId !== reqIdRef.current) return;

      if (dbError) {
        setError(dbError.message);
        setSub(null);
      } else {
        setSub(data as Subscription | null);
      }
    } catch (err) {
      if (!mountedRef.current || reqId !== reqIdRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load subscription");
      setSub(null);
    } finally {
      if (mountedRef.current && reqId === reqIdRef.current) {
        setLoading(false);
      }
    }
  }, [session?.user?.id]);

  useEffect(() => {
    fetchSub();
  }, [fetchSub]);

  const { level: accessLevel, isActive, plan, status } = resolveAccessLevel(sub);

  return {
    plan,
    status,
    accessLevel,
    isActive,
    isComplimentary: hasComplimentaryAccess(sub),
    endDate: sub?.end_date ?? null,
    trialEnd: sub?.trial_end ?? null,
    subscription: sub,
    loading,
    error,
    retry: fetchSub,
  };
}
